import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import weplLogo from '../assets/images/wepl_logo.png';
import { formatPhoneForDisplay, phoneToTelHref } from '../utils/phoneUtils';
import './PublicCardPage.css';

const API_BASE = (() => {
  const env = process.env.REACT_APP_API_URL;
  if (env) return env;
  if (process.env.NODE_ENV !== 'production') return '/api/v1';
  return '/api/v1';
})();

/* ------------------------------------------------------------------ */
/*  SVG icon helpers                                                   */
/* ------------------------------------------------------------------ */
const SaveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1-2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
);
const ShareIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/></svg>
);
const EmailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
);
const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
);
const MobileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/></svg>
);
const LinkedInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg>
);
const CardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="14" x="3" y="5" rx="2"/><path d="M3 10h18"/></svg>
);

/* ------------------------------------------------------------------ */
/*  Loading skeleton                                                   */
/* ------------------------------------------------------------------ */
function LoadingSkeleton() {
  return (
    <div className="public-card-root">
      <div className="pc-card">
        <div className="pc-accent-bar" />
        <div className="pc-loading-body">
          <div className="pc-skeleton" style={{ width: 100, height: 12, marginBottom: 24 }} />
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div className="pc-skeleton" style={{ width: 72, height: 72, borderRadius: '50%', margin: '0 auto 14px' }} />
            <div className="pc-skeleton" style={{ width: 180, height: 20, margin: '0 auto 6px' }} />
            <div className="pc-skeleton" style={{ width: 140, height: 14, margin: '0 auto' }} />
          </div>
          <div className="pc-skeleton" style={{ width: '100%', height: 48, borderRadius: 12, marginBottom: 8 }} />
          <div className="pc-skeleton" style={{ width: '100%', height: 48, borderRadius: 12 }} />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Unavailable state                                                  */
/* ------------------------------------------------------------------ */
function CardUnavailable() {
  return (
    <div className="public-card-root">
      <div className="pc-card">
        <div className="pc-accent-bar--unavailable" />
        <div className="pc-unavailable-body">
          <div className="pc-icon-circle"><CardIcon /></div>
          <h1>Card Unavailable</h1>
          <p>This digital name card is no longer available. The owner may have deactivated or removed it.</p>
        </div>
        <div className="pc-unavailable-footer">Winner Engineering</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main public card page                                              */
/* ------------------------------------------------------------------ */
export default function PublicCardPage() {
  const { uuid } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [canShare, setCanShare] = useState(false);

  useEffect(() => {
    setCanShare(typeof navigator !== 'undefined' && !!navigator.share);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    fetch(`${API_BASE}/public/card/${uuid}/`)
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setStatus('ok');
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('unavailable');
      });

    return () => { cancelled = true; };
  }, [uuid]);

  useEffect(() => {
    if (status !== 'ok' || !data) return;
    const suffix = data.designation ? ` \u2013 ${data.designation}` : '';
    document.title = `${data.name}${suffix} | Digital Card`;
  }, [status, data]);

  const handleShare = useCallback(() => {
    if (!navigator.share || !data) return;
    navigator.share({
      title: data.name,
      text: `Contact card for ${data.name}`,
      url: window.location.href,
    }).catch(() => {});
  }, [data]);

  if (status === 'loading') return <LoadingSkeleton />;
  if (status === 'unavailable') return <CardUnavailable />;

  const hasContact = data.work_email || data.work_phone || data.personal_phone || data.linkedin_url;

  return (
    <div className="public-card-root">
      <div className="pc-card">
        {/* Hero: midnight canvas with brand wordmark */}
        <div className="pc-hero">
          <div className="pc-company-brand">
            <img src={weplLogo} alt={data.company_name} className="pc-company-logo" />
            <span>{data.company_name}</span>
          </div>
        </div>

        {/* Identity overlapping hero */}
        <div className="pc-identity">
          <div className="pc-avatar"><span>{data.initials}</span></div>
          <h1>{data.name}</h1>
          {data.designation && <div className="pc-title">{data.designation}</div>}
          {data.department && (
            <div className="pc-org">{data.department}</div>
          )}
        </div>

        {/* Contact details */}
        {hasContact && (
          <div className="pc-contacts">
            {data.work_email && (
              <a href={`mailto:${data.work_email}`} className="pc-contact-row">
                <div className="pc-contact-icon pc-contact-icon--email"><EmailIcon /></div>
                <div className="pc-contact-info">
                  <div className="pc-contact-label">Email</div>
                  <div className="pc-contact-value">{data.work_email}</div>
                </div>
              </a>
            )}
            {data.work_phone && (
              <a href={phoneToTelHref(data.work_phone)} className="pc-contact-row">
                <div className="pc-contact-icon pc-contact-icon--phone"><PhoneIcon /></div>
                <div className="pc-contact-info">
                  <div className="pc-contact-label">
                    Office{data.extension ? ` \u00B7 Ext ${data.extension}` : ''}
                  </div>
                  <div className="pc-contact-value">{formatPhoneForDisplay(data.work_phone)}</div>
                </div>
              </a>
            )}
            {data.personal_phone && (
              <a href={phoneToTelHref(data.personal_phone)} className="pc-contact-row">
                <div className="pc-contact-icon pc-contact-icon--mobile"><MobileIcon /></div>
                <div className="pc-contact-info">
                  <div className="pc-contact-label">Mobile</div>
                  <div className="pc-contact-value">{formatPhoneForDisplay(data.personal_phone)}</div>
                </div>
              </a>
            )}
            {data.linkedin_url && (
              <a href={data.linkedin_url} target="_blank" rel="noopener noreferrer" className="pc-contact-row">
                <div className="pc-contact-icon pc-contact-icon--linkedin"><LinkedInIcon /></div>
                <div className="pc-contact-info">
                  <div className="pc-contact-label">LinkedIn</div>
                  <div className="pc-contact-value">View Profile</div>
                </div>
              </a>
            )}
          </div>
        )}

        {/* Action buttons — pill shaped */}
        <div className="pc-actions">
          <a href={data.vcard_url} className="pc-btn-primary" download>
            <SaveIcon /> <span>Save Contact</span>
          </a>
          {canShare && (
            <button type="button" className="pc-btn-outline" onClick={handleShare}>
              <ShareIcon /> <span>Share</span>
            </button>
          )}
        </div>

        {/* Footer — subtle luxury sign-off */}
        <div className="pc-privacy">
          Private Card <span className="pc-privacy-mark">&bull;</span> No data collected
        </div>
      </div>
    </div>
  );
}
